import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner, Form } from 'react-bootstrap';
import { getSurveyResponseDetails } from 'services/reports';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

const SurveyResponseDetailsModal = ({
  surveyResponseId,
  show,
  handleClose
}) => {
  const [responseDetails, setResponseDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!surveyResponseId || !show) return;

    setLoading(true);
    getSurveyResponseDetails(surveyResponseId)
      .then(response => {
        if (response.result) {
          setResponseDetails(response.data);
        } else {
          toast.error('Error al cargar los detalles de la respuesta.', {
            theme: 'colored'
          });
        }
      })
      .catch(error => {
        console.error('Error al cargar los detalles de la respuesta:', error);
        toast.error('Error al cargar los detalles de la respuesta.', {
          theme: 'colored'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [surveyResponseId, show]);

  const renderQuestion = (question, responses) => {
    const response = responses.find(res => res.id === question.id) || {};
    const {
      selected_options = [],
      answer_text,
      status = 'Sin respuesta'
    } = response;

    if (question.type.name === 'Opción única') {
      return (
        <Form.Group key={question.id} className="mb-3">
          <Form.Label className="fw-bold">
            {question.text}
            {status === 'Sin respuesta' && (
              <span className="text-danger ms-2">(No respondida)</span>
            )}
          </Form.Label>
          {question.available_options.map(option => (
            <Form.Check
              key={option.id}
              type="radio"
              label={option.text}
              name={`question-${question.id}`}
              checked={selected_options.some(
                selected => selected.id === option.id
              )}
              disabled
            />
          ))}
        </Form.Group>
      );
    }

    if (question.type.name === 'Opción múltiple') {
      return (
        <Form.Group key={question.id} className="mb-3">
          <Form.Label className="fw-bold">
            {question.text}
            {status === 'Sin respuesta' && (
              <span className="text-danger ms-2">(No respondida)</span>
            )}
          </Form.Label>
          {question.available_options.map(option => (
            <Form.Check
              key={option.id}
              type="checkbox"
              label={option.text}
              checked={selected_options.some(
                selected => selected.id === option.id
              )}
              disabled
            />
          ))}
        </Form.Group>
      );
    }

    if (question.type.name === 'Respuesta abierta') {
      return (
        <Form.Group key={question.id} className="mb-3">
          <Form.Label className="fw-bold">
            {question.text}
            {status === 'Sin respuesta' && (
              <span className="text-danger ms-2">(No respondida)</span>
            )}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={answer_text || ''}
            disabled
            className="fs-10"
          />
        </Form.Group>
      );
    }

    return null;
  };

  const renderQuestionGroup = (group, responses) => {
    return (
      <div key={group.id} className="mb-4">
        <h6 className="fw-bold">{group.title}</h6>
        <p className="text-muted fs-10">{group.description}</p>
        {group.questions.map(question => renderQuestion(question, responses))}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">Detalles de la Respuesta</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" role="status" className="text-primary">
              <span className="visually-hidden">Cargando...</span>
            </Spinner>
          </div>
        ) : responseDetails ? (
          <>
            <h6 className="fw-bold mb-2">{responseDetails.survey.name}</h6>
            <p className="text-muted fs-10 mb-3">
              {responseDetails.survey.description}
            </p>
            {responseDetails.survey.question_groups.map(group =>
              renderQuestionGroup(group, responseDetails.responses)
            )}
          </>
        ) : (
          <p className="text-center text-muted fs-10">
            No se encontraron detalles para esta respuesta.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

SurveyResponseDetailsModal.propTypes = {
  surveyResponseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  show: PropTypes.bool,
  handleClose: PropTypes.func
};

export default SurveyResponseDetailsModal;
